import { AnyExtension, Content, Editor, EditorOptions } from '@tiptap/core';
import { Bold } from '@tiptap/extension-bold';
import { Document } from '@tiptap/extension-document';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Text } from '@tiptap/extension-text';
import { useEditor } from '@tiptap/react';
import { MutableRefObject, useImperativeHandle, useMemo, useRef } from 'react';

import { fetchAmounts, fetchHashtags } from '../lib/data';
import { Budget } from '../lib/entities/budget';
import { User } from '../lib/entities/user';
import { Amount } from '../lib/extensions/amount';
import { Emoji } from '../lib/extensions/emoji';
import { FileDrop } from '../lib/extensions/file-drop';
import { Hashtag } from '../lib/extensions/hashtag';
import { MarkdownLink } from '../lib/extensions/markdown-link';
import { Mention } from '../lib/extensions/mention';
import { Recipient } from '../lib/extensions/recipient';
import { SuggestionHighlight } from '../lib/extensions/suggestion-highlight';
import { RichMediaState } from '../lib/state/rich-media-state';
import { createEmojiTransformer } from '../lib/transformers';

const NonInclusiveLink = Link.extend({
	inclusive: false,
});

export type ModuleKey = 'amount' | 'emoji' | 'hashtag' | 'image' | 'link' | 'mention' | 'recipient' | 'attachment' | 'suggestion-highlight';

type Fetcher<T> = (config: { editor: Editor; query: string; state: RichMediaState }) => Promisable<T>;

export type RichInputConfig = Partial<EditorOptions> & {
	allowanceBoostId?: string;
	autofocus?: boolean | 'end';
	budget?: Budget;
	content?: Content;
	editable?: boolean;
	fetchMentions?: Fetcher<User[]>;
	fetchRecipients?: Fetcher<User[]>;
	options?: Partial<Record<ModuleKey, any>>;
	modules?: ModuleKey[];
	placeholder?: string | null;
	postFile?: (file: File) => void;
	ref?: MutableRefObject<RichInputHandle | null>;
};

export interface RichInputHandle {
	focus: VoidFunction;
	insert: (content: Content) => void;
	insertLink: VoidFunction;
	reset: VoidFunction;
}

const defaultCofig: RichInputConfig = {
	fetchMentions: () => [],
	fetchRecipients: () => [],
	modules: [],
	postFile: () => void 0,
};

export const useRichInput = ({
	allowanceBoostId,
	autofocus = false,
	budget,
	content,
	editable = true,
	fetchMentions,
	fetchRecipients,
	modules = [],
	options = {},
	placeholder,
	postFile,
	ref,
	...rest
}: RichInputConfig = defaultCofig) => {
	const uniqueIdRef = useRef(Math.random().toString(36).slice(2, 7));

	const uniqueId = uniqueIdRef.current;

	const extensions = useMemo(() => {
		const getOptions = (key: ModuleKey) => options[key] || {};

		const Modules: Record<ModuleKey, AnyExtension | AnyExtension[]> = {
			'suggestion-highlight': SuggestionHighlight,
			amount: Amount.configure({
				allowanceBoostId,
				budget,
				fetchData: fetchAmounts,
				uniqueId: uniqueIdRef.current,
				...getOptions('amount'),
			}),
			hashtag: Hashtag.configure({
				fetchData: fetchHashtags,
				uniqueId,
				...getOptions('hashtag'),
			}),
			image: FileDrop.configure({
				allowedExtensions: ['jpg', 'jpeg', 'png', 'gif'],
				onDrop: postFile,
				...getOptions('image'),
			}),
			attachment: FileDrop.configure({
				allowedExtensions: ['avi', 'jpg', 'jpeg', 'mov', 'mp4', 'png', 'gif', 'webm', 'wmv'],
				onDrop: postFile,
				...getOptions('attachment'),
			}),
			link: [
				NonInclusiveLink.configure({
					autolink: true,
					linkOnPaste: true,
					openOnClick: false,
					HTMLAttributes: {
						rel: 'noopener noreferrer',
						target: '_blank',
					},
				}),
				MarkdownLink.configure(getOptions('link')),
			],
			mention: Mention.configure({
				fetchData: fetchMentions,
				uniqueId,
				...getOptions('mention'),
			}),
			recipient: Recipient.configure({
				fetchData: fetchRecipients,
				uniqueId,
				...getOptions('recipient'),
			}),
			emoji: Emoji.configure({
				transformContent: createEmojiTransformer(),
				...getOptions('emoji'),
			}),
		};

		const extensions = Object.entries(Modules)
			.filter(([key]) => modules.includes(key as ModuleKey))
			.flatMap(([, module]) => (Array.isArray(module) ? module : [module]));

		if (placeholder) {
			extensions.push(Placeholder.configure({ placeholder }));
		}

		return [Document, Paragraph, Text, Bold, Italic, ...extensions];
	}, [allowanceBoostId, budget, fetchMentions, fetchRecipients, modules, options, placeholder, postFile, uniqueId]);

	const editor = useEditor({
		...rest,
		autofocus,
		content,
		editable,
		extensions,
	});

	useImperativeHandle(
		ref,
		() => ({
			focus: () => {
				editor?.commands.focus();
			},
			insert: (content: Content) => {
				editor?.commands.insertContent(content);
			},
			insertLink: () => {
				editor?.commands.insertLink();
			},
			reset: () => {
				editor?.commands.clearContent();
			},
		}),
		[editor]
	);

	return { editor, uniqueId: uniqueIdRef.current };
};
